import { useRoles } from '@/hooks';
import { getDateAsString } from '@/lib';
import { useOrganizations } from '@/pages/Organizations/hooks/useOrganizations';
import detailsPageStyles from '@/styles/components/details.page.module.css';
import type { UserPublic } from '@/types';
import { Badge } from '@nautilusbio/nb-design-system/Badge';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { Mail } from 'lucide-react';
import React, { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './UserDetails.module.css';
import { inferPrimaryOrgId, inferPrimaryRole } from './useUserDetail';

type UserHeaderCardProps = {
  user: UserPublic;
};

export const UserHeaderCard: FC<UserHeaderCardProps> = ({ user }) => {
  const { t: tUsers } = useTranslation('users');
  const { t: tGeneral } = useTranslation('general');
  const { data: orgs = [] } = useOrganizations();
  const orgNameMap = useMemo(() => new Map(orgs.map(o => [o.id, o.name])), [orgs]);
  // Per the design annotation, Organization is shown only to a super user — a property of the
  // viewer, not of the user being viewed.
  const { isGlobalSuperAdmin } = useRoles();

  const primaryOrgId = inferPrimaryOrgId(user);
  const primaryRole = primaryOrgId ? inferPrimaryRole(user, primaryOrgId) : undefined;
  const primaryOrgName = primaryOrgId ? orgNameMap.get(primaryOrgId) : undefined;
  const canSeeOrganization = isGlobalSuperAdmin();

  return (
    <Stack
      alignItems="stretch"
      className={clsx(styles.headerCard, detailsPageStyles.header)}
      gap={6}
      orientation="horizontal"
    >
      {/* Left column */}
      <Stack className={styles.headerCardLeft} gap={3} orientation="vertical">
        <Text as="h1" className={styles.fullName} size="xl2">
          {user.full_name}
        </Text>

        <Stack alignItems="center" className={styles.fieldRow} gap={2} orientation="horizontal">
          <Mail size={14} />
          <Text as="a" href={`mailto:${user.email}`}>
            {user.email}
          </Text>
        </Stack>

        <Stack gap={6} orientation="horizontal">
          <Stack orientation="vertical">
            <Text as="span" className={styles.fieldLabel}>
              {tUsers('primary_role')}
            </Text>
            <Text as="span">{primaryRole ?? '—'}</Text>
          </Stack>

          {canSeeOrganization && (
            <Stack orientation="vertical">
              <Text as="span" className={styles.fieldLabel}>
                {tUsers('organization_column_header')}
              </Text>
              {primaryOrgId && primaryOrgName ? (
                <Link
                  data-testid="user-detail-organization-link"
                  params={{ orgId: primaryOrgId }}
                  to="/organizations/$orgId"
                >
                  <Text as="span">{primaryOrgName}</Text>
                </Link>
              ) : (
                <Text as="span">—</Text>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>

      {/* Right column — 2-col grid with vertical divider, matching Study details stats style */}
      <Grid
        className={styles.headerCardRight}
        gridTemplateColumns="1fr auto 1fr"
        gridTemplateRows="auto auto"
        justifyItems="center"
        rowGap={4}
      >
        <Stack alignItems="center" gap={2} orientation="vertical">
          <Text as="span" className={styles.statLabel} noWrap>
            {tGeneral('updated_at')}
          </Text>
          <Text as="span" noWrap size="lg">
            {user.updated_at ? getDateAsString(new Date(user.updated_at)) : '—'}
          </Text>
        </Stack>

        {/* Vertical divider — grid-column:2 grid-row:1/span 2 via CSS; auto-placement flows around it */}
        <div aria-hidden className={styles.statDivider} />

        <Stack alignItems="center" gap={2} orientation="vertical">
          <Text as="span" className={styles.statLabel} noWrap>
            {tGeneral('created_at')}
          </Text>
          <Text as="span" noWrap size="lg">
            {user.created_at ? getDateAsString(new Date(user.created_at)) : '—'}
          </Text>
        </Stack>

        <Stack alignItems="center" gap={2} orientation="vertical">
          <Text as="span" className={styles.statLabel} noWrap>
            {tUsers('status')}
          </Text>
          <Badge size="xs" variant={user.is_active ? 'success' : 'default'}>
            {user.is_active ? tUsers('status_active') : tUsers('status_inactive')}
          </Badge>
        </Stack>

        <Stack alignItems="center" gap={2} orientation="vertical">
          <Text as="span" className={styles.statLabel} noWrap>
            {tUsers('last_login_at_column_header')}
          </Text>
          <Text as="span" noWrap size="lg">
            {user.last_login_at ? getDateAsString(new Date(user.last_login_at)) : '—'}
          </Text>
        </Stack>
      </Grid>
    </Stack>
  );
};
